import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays } from "lucide-react";
import { resolveMedia } from "../siteContext";
import { useLang, pick } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

export const BlogSection = () => {
  const { lang, s } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/posts`).then((res) => setPosts(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  }, []);

  if (!posts.length) return null;
  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;
  const shown = posts.slice(0, 3);

  return (
    <section id="blog" data-testid="blog-section" className="py-28 md:py-36 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease }}
          className="max-w-3xl mb-16"
        >
          <p className="text-luxury-gold text-sm tracking-[0.35em] mb-6">{s.blog_eyebrow}</p>
          <h2 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-luxury-bone leading-[1.25]">
            {s.blog_title_a} <span className="gold-gradient-text italic">{s.blog_title_b}</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {shown.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`blog-card-${i}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.12, duration: 0.8, ease }}
              className="group bg-luxury-surface border border-luxury-border rounded-2xl overflow-hidden hover:border-luxury-gold hover:-translate-y-2 transition-all duration-500 flex flex-col"
            >
              {p.cover_image ? (
                <div className="h-52 overflow-hidden">
                  <img src={resolveMedia(p.cover_image)} alt={pick(lang, p, "title")} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </div>
              ) : null}
              <div className="p-7 flex flex-col flex-1">
                <p className="text-luxury-gray text-xs flex items-center gap-2 mb-3">
                  <CalendarDays className="w-3.5 h-3.5 text-luxury-gold" strokeWidth={1.5} />
                  {(p.created_at || "").slice(0, 10)}
                </p>
                <h3 className="font-serif text-2xl text-luxury-bone mb-3 leading-snug">{pick(lang, p, "title")}</h3>
                <p className="text-luxury-gray text-sm leading-relaxed flex-1 line-clamp-3">{pick(lang, p, "excerpt")}</p>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-read-${i}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm text-luxury-gold hover:text-luxury-goldLight transition-colors"
                >
                  {s.blog_read}
                  <Arrow className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>

        {posts.length > 3 && (
          <div className="text-center mt-12">
            <Link to="/blog" data-testid="blog-all-link" className="inline-flex items-center gap-2 border border-luxury-gold/60 text-luxury-gold px-8 py-3.5 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors duration-300 text-sm font-semibold">
              {s.blog_all}
              <Arrow className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
